import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserData } from '../types/user';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import {
  ImageField,
  countries
} from '../shared/formData';
import { usernameService } from '../services/usernameService';
import { analyticsService } from '../services/analyticsService';
import { profileImageCache } from '../utils/imageCache';
import { HiInformationCircle } from 'react-icons/hi2';
import SearchableSelect from './common/SearchableSelect';

const CompleteProfile: React.FC = () => {
  const { authState, currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [localError, setLocalError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showGenderInfoDialog, setShowGenderInfoDialog] = useState<boolean>(false);
  
  // Form fields
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  const [country, setCountry] = useState('');
  const [reason, setReason] = useState('');
  const [cycleDays, setCycleDays] = useState(7);
  const [blockDays, setBlockDays] = useState(28);
  const [campaignCode, setCampaignCode] = useState('');
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);

  // Effect to create image preview when file is selected
  useEffect(() => {
    if (profileImage) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(profileImage);
    } else {
      setImagePreview(null);
    }
  }, [profileImage]);

  // Redirect to home if not authenticated
  if (!authState.isAuthenticated) {
    navigate('/');
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setLoading(true);
    
    // Validation
    if (!username || !name || !surname || !gender || !dob || !country || !reason) {
      setLocalError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    // Check username availability
    setIsCheckingUsername(true);
    setUsernameError(null);
    
    try {
      const usernameCheck = await usernameService.checkUsernameAvailability(username);
      if (!usernameCheck.available) {
        setUsernameError(usernameCheck.message);
        setLocalError(usernameCheck.message);
        setLoading(false);
        setIsCheckingUsername(false);
        return;
      }
    } catch (error) {
      setLocalError('Error checking username availability. Please try again.');
      setLoading(false);
      setIsCheckingUsername(false);
      return;
    }
    
    setIsCheckingUsername(false);

    if (cycleDays < 1 || blockDays < 1) {
      setLocalError('Cycle and block days must be at least 1');
      setLoading(false);
      return;
    }
    
    try {
      if (!currentUser) {
        throw new Error("Not authenticated");
      }

      // Create user data object
      const userData: UserData = {
        username,
        email: currentUser.email || '',
        name,
        surname,
        gender,
        dob,
        country,
        reason,
        cycleDays,
        blockDays,
        ...(campaignCode && { campaignCode })
      };
      
      // Save to Firestore using userService (handles image upload automatically)
      await userService.completeUserRegistration(
        currentUser.uid,
        userData,
        profileImage || undefined
      );
      
      // Refresh AuthContext to get the updated profile data immediately
      await refreshUserData();
      
      // Track profile completion in analytics
      analyticsService.trackProfileComplete(currentUser.uid);
      
      // Navigate back to home page
      navigate('/');
    } catch (error) {
      setLocalError((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-base-200 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-base-content mb-2">
            Complete Your Profile
          </h1>
          <p className="text-base-content/70 text-lg">
            Tell us a bit more about yourself to get started
          </p>
        </div>

        {/* Main Form Card */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body p-8">
            {/* Error Alert */}
            {localError && (
              <div className="alert alert-error mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{localError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Profile Image Section */}
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4">Profile Photo</h3>
                <ImageField
                  value={profileImage}
                  previewUrl={imagePreview}
                  onChange={setProfileImage}
                  disabled={loading}
                />
              </div>

              {/* Personal Information */}
              <div className="divider">
                <span className="text-lg font-semibold">Personal Information</span>
              </div>

              {/* Username field - full width */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Username *</span>
                </label>
                <input
                  type="text"
                  className={`input input-bordered w-full ${
                    usernameError ? 'input-error' : ''
                  }`}
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setUsernameError(null);
                  }}
                  disabled={loading}
                  placeholder="Enter your unique username"
                  required
                />
                {usernameError && (
                  <label className="label">
                    <span className="label-text-alt text-error">{usernameError}</span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt">Choose a unique username (3-20 characters, letters, numbers, and underscores only)</span>
                </label>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">First Name *</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                    placeholder="Enter your first name"
                    required
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Last Name *</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                    disabled={loading}
                    placeholder="Enter your last name"
                    required
                  />
                </div>

                <div className="form-control">
                  <SearchableSelect
                    options={[
                      { value: "Male", label: "Male" },
                      { value: "Female", label: "Female" },
                    ]}
                    value={gender}
                    onChange={(value) => setGender(value)}
                    placeholder="Search..."
                    allOptionLabel="Select Gender"
                    emptyValue=""
                    disabled={loading}
                    label="Gender *"
                    inputClassName="bg-base-200"
                  />
                  <div className="mt-2 flex items-start gap-1.5 justify-center">
                    <button
                      type="button"
                      onClick={() => setShowGenderInfoDialog(true)}
                      className="flex items-center gap-1 text-sm underline text-base-content/80 hover:text-base-content transition-colors"
                    >
                      <span>Why we ask for your sex?</span>
                    </button>
                  </div>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Date of Birth *</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered w-full"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    disabled={loading}
                    required
                  />
                  <label className="label">
                    <span className="label-text-alt">Format: DD/MM/YYYY</span>
                  </label>
                </div>

                <div className="form-control">
                  <SearchableSelect
                    options={countries.map((countryName) => ({
                      value: countryName,
                      label: countryName,
                    }))}
                    value={country}
                    onChange={(value) => setCountry(value)}
                    placeholder="Search countries..."
                    allOptionLabel="Select Country"
                    emptyValue=""
                    disabled={loading}
                    label="Country *"
                    inputClassName="bg-base-200"
                  />
                </div>

              </div>

              {/* Athletic Information */}
              <div className="divider">
                <span className="text-lg font-semibold">Athletic Profile</span>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="form-control">
                  <SearchableSelect
                    options={[
                      { value: "I want to train like an Athlete", label: "I want to train like an Athlete" },
                      { value: "Better Health, Training & Wellness Monitoring", label: "Better Health, Training & Wellness Monitoring" },
                      { value: "Just curious", label: "Just curious" },
                      { value: "I watch Tori on TikTok", label: "I watch Tori on TikTok" },
                      { value: "I want to learn how I can optimise my performance", label: "I want to learn how I can optimise my performance" },
                      { value: "Other", label: "Other" },
                    ]}
                    value={reason}
                    onChange={(value) => setReason(value)}
                    placeholder="Search reasons..."
                    allOptionLabel="Select Reason"
                    emptyValue=""
                    disabled={loading}
                    label="Reason for Joining *"
                    inputClassName="bg-base-200"
                  />
                </div>
              </div>

              {/* Training Configuration */}
              <div className="divider">
                <span className="text-lg font-semibold">Training Configuration</span>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Cycle Length (Days) *</span>
                  </label>
                  <input
                    type="number"
                    className="input input-bordered w-full"
                    value={cycleDays}
                    onChange={(e) => setCycleDays(parseInt(e.target.value) || 7)}
                    disabled={loading}
                    placeholder="7"
                    min="1"
                    max="365"
                    required
                  />
                  <label className="label">
                    <span className="label-text-alt">How many days in your training cycle</span>
                  </label>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Block Length (Days) *</span>
                  </label>
                  <input
                    type="number"
                    className="input input-bordered w-full"
                    value={blockDays}
                    onChange={(e) => setBlockDays(parseInt(e.target.value) || 28)}
                    disabled={loading}
                    placeholder="28"
                    min="1"
                    max="365"
                    required
                  />
                  <label className="label">
                    <span className="label-text-alt">How many days in your training block</span>
                  </label>
                </div>

                <div className="form-control lg:col-span-2">
                  <label className="label">
                    <span className="label-text font-medium">Campaign Code</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={campaignCode}
                    onChange={(e) => setCampaignCode(e.target.value)}
                    disabled={loading}
                    placeholder="Enter your campaign code (optional)"
                  />
                  <label className="label">
                    <span className="label-text-alt">Optional: Enter any promotional or referral code</span>
                  </label>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <button 
                  type="button"
                  onClick={() => navigate('/')}
                  disabled={loading || isCheckingUsername}
                  className="btn !btn-ghost btn-lg order-2 sm:order-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || isCheckingUsername}
                  className={`btn btn-primary btn-lg order-1 sm:order-2 ${(loading || isCheckingUsername) ? 'loading' : ''}`}
                >
                  {isCheckingUsername ? (
                    <>
                      <span className="loading loading-spinner"></span>
                      Checking Username...
                    </>
                  ) : loading ? (
                    <>
                      <span className="loading loading-spinner"></span>
                      Saving Profile...
                    </>
                  ) : (
                    'Save Profile'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Gender Info Dialog */}
      {showGenderInfoDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-base-content mb-4">
              Why we ask for your gender?
            </h3>
            <p className="text-base-content/80 mb-6">
              PEAK^ uses biological sex to help interpret your physiological data accurately. 
              <br /><br />
              By choosing an option that reflects your underlying physiology, you ensure your readiness scores and trends are correct.
              <br /><br />
              PEAK^ does not use this information for identification purposes or social features. It is stored securely and used solely for improving the accuracy of your insights.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowGenderInfoDialog(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompleteProfile; 